import { forwardRef, RefObject } from 'react';
import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  Portal,
  Stack,
} from '@chakra-ui/react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import CurrencyInput, { formatValue } from 'react-currency-input-field';
import DropdownMultiSelectInput from 'app/components/UI/Inputs/DropdownMultiSelectInput';
import { ArrowRightOutline, DropdownInput, ArrowLeftOutline } from '@aslanpay/portal-common-lib';
import { useCreatePayCalendarStore } from 'app/store';
import { payCalendarCalendarDefaultsFormSchema } from 'app/utils/schemas';

import {
  getLocationOptions,
  getNonBusinessDayFailoverOptions,
  getWorkingDaysOptions,
} from 'app/components/PayCalendars/options';

import { parseCurrencyIntoFloat } from 'app/utils/strings';

import { PayCalendarCalendarDefaultsFormFields, PayCalendarFormProps } from '../types';

const PayCalendarCalendarDefaultsForm = forwardRef<HTMLElement, PayCalendarFormProps>(
  ({ id, onStepAction, previousStep, canPrevStep }, ref) => {
    const payCalendarData = useCreatePayCalendarStore((state) => state.payCalendarData);
    const setPayCalendarData = useCreatePayCalendarStore((state) => state.setPayCalendarData);

    const {
      register,
      control,
      handleSubmit,
      getValues,
      formState: { errors, isValid },
    } = useForm<PayCalendarCalendarDefaultsFormFields>({
      mode: 'onChange',
      defaultValues: {
        ...payCalendarData,
        defaultAnnualGrossSalary: formatValue({
          value: (
            payCalendarData as PayCalendarCalendarDefaultsFormFields
          ).defaultAnnualGrossSalary?.toString(),
          prefix: '\u00A3',
          groupSeparator: ',',
          decimalSeparator: '.',
          decimalScale: 2,
        }),
      },
      resolver: yupResolver(payCalendarCalendarDefaultsFormSchema),
    });

    // populate optional selections
    const workingDaysOptions = getWorkingDaysOptions();
    const paydayNonBusinessDayFailoverOptions = getNonBusinessDayFailoverOptions();
    const locationOptions = getLocationOptions();

    // Submit handler, moves onto next step, Step 4
    const handleNextStep = (data: PayCalendarCalendarDefaultsFormFields) => {
      setPayCalendarData({
        ...data,
        defaultAnnualGrossSalary: data.defaultAnnualGrossSalary
          ? data.defaultAnnualGrossSalary
          : undefined,
      });
      onStepAction();
    };

    const handleBackCLick = () => {
      const formValues = getValues();
      const getValuesSalary = getValues('defaultAnnualGrossSalary');
      const parsedSalary = getValuesSalary ? parseCurrencyIntoFloat(getValuesSalary) : undefined;
      setPayCalendarData({
        ...formValues,
        defaultAnnualGrossSalary: parsedSalary ? parsedSalary.toString() : undefined,
      });
      previousStep();
    };

    return (
      <form id={id} onSubmit={handleSubmit(handleNextStep)} noValidate>
        <Stack spacing="20px">
          <FormControl isInvalid={!!errors?.workingDays?.message} isRequired>
            <FormLabel>Working days</FormLabel>
            <Controller
              control={control}
              name="workingDays"
              render={({ field: { onChange, onBlur, value } }) => (
                <DropdownMultiSelectInput
                  placeholder="Select one or more working days"
                  defaultValue={value}
                  options={workingDaysOptions}
                  onChange={onChange}
                  onBlur={onBlur}
                />
              )}
            />
            <FormErrorMessage>{errors?.paydayNonBusinessDayFailover?.message}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={!!errors?.paydayNonBusinessDayFailover?.message} isRequired>
            <FormLabel>Default payday action</FormLabel>
            <Controller
              control={control}
              name="paydayNonBusinessDayFailover"
              render={({ field: { onChange, onBlur, value } }) => (
                <DropdownInput
                  placeholder="If payday lands on a weekend or bank holiday"
                  defaultValue={paydayNonBusinessDayFailoverOptions.find(
                    (paydayNonBusinessDayFailover) => paydayNonBusinessDayFailover.value === value
                  )}
                  options={paydayNonBusinessDayFailoverOptions}
                  onChange={onChange}
                  onBlur={onBlur}
                />
              )}
            />
            <FormErrorMessage>{errors?.paydayNonBusinessDayFailover?.message}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={!!errors?.location?.message} isRequired>
            <FormLabel>Location (country)</FormLabel>
            <Controller
              control={control}
              name="location"
              render={({ field: { onChange, onBlur, value } }) => (
                <DropdownInput
                  placeholder="In which country are the employees based?"
                  defaultValue={locationOptions.find((location) => location.value === value)}
                  options={locationOptions}
                  onChange={onChange}
                  onBlur={onBlur}
                />
              )}
            />
            <FormErrorMessage>{errors?.location?.message}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={!!errors?.defaultAnnualGrossSalary?.message}>
            <FormLabel>Default salary amount (optional)</FormLabel>
            <InputGroup>
              <Input
                defaultValue={
                  (payCalendarData as PayCalendarCalendarDefaultsFormFields)
                    ?.defaultAnnualGrossSalary
                }
                as={CurrencyInput}
                prefix={'\u00A3'}
                decimalSeparator="."
                groupSeparator=","
                decimalScale={2}
                type="text"
                placeholder={'Default salary for created employees. E.g. \u00A324,000.00'}
                {...register('defaultAnnualGrossSalary')}
              />
            </InputGroup>
            <FormErrorMessage>{errors?.defaultAnnualGrossSalary?.message}</FormErrorMessage>
          </FormControl>
          <Portal containerRef={ref as RefObject<HTMLElement>}>
            <Button
              size="sm"
              variant="outline"
              isDisabled={!canPrevStep}
              leftIcon={<ArrowLeftOutline boxSize="20px" />}
              onClick={handleBackCLick}
              mr="16px"
            >
              Back
            </Button>
            <Button
              form={id}
              type="submit"
              size="sm"
              isDisabled={!isValid}
              rightIcon={<ArrowRightOutline boxSize="20px" />}
            >
              Next
            </Button>
          </Portal>
        </Stack>
      </form>
    );
  }
);

PayCalendarCalendarDefaultsForm.displayName = 'PayCalendarCalendarDefaultsForm';

export default PayCalendarCalendarDefaultsForm;
